import AddIcon from "@mui/icons-material/Add";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useProgramPreviewIdentity } from "@bw/shared/hooks";
import type { ReservationQuoteRequest } from "@bw/shared/types";
import { formatMoney, parseDecimal } from "@bw/shared/utils";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from "react";
import { notifications } from "@/app/notifications";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  createPriceOverride,
  deletePriceOverride,
  deletePricingRule,
  listPriceOverrides,
  listPricingRules,
  type MembershipPriceOverride,
  type PricingRule,
} from "@/libs/ratesApi";
import { listCourts, listMembershipPlans } from "@/libs/facilityApi";
import { getReservationQuote, type QuoteResponse } from "@/libs/quoteApi";
import { RatesList } from "./rates-list";
import { RuleEditor } from "./rule-editor";
import { SimpleRatesMode } from "./simple-rates-mode";
import { MONEY, type CourtOption, type PlanOption } from "./schema";
import type { OverrideState } from "./member-pricing-row";

// The Rates workspace — the orchestrator. It owns the page chrome, the rule
// list, the focused grouped editor (RuleEditor), the per-rule member-pricing
// expand (MemberPricingRow, via RatesList), and the reservation quote preview.
// The FE owns NO pricing logic: it forwards what the operator types and renders
// what the backend returns. Ordering is set by the list's accessible Move
// up/down (movePricingRule) — there is no typed-priority field anywhere.

// The editor's open/target state: undefined = closed, null = create a new rule,
// a PricingRule = edit that rule.
type EditorState = PricingRule | null | undefined;

// The "None" sentinel only lives in the quote Plan select's value space — a
// Radix SelectItem cannot have an empty-string value, so the absence of a plan
// is this token in the control and is translated to omit on the wire (never sent
// as a "none" string). It is NOT a plan id.
const NO_PLAN = "__none__";

// The simulator's State-union. Idle = open, no result yet; loading = request in
// flight; error = the request failed (honest message, NO fabricated numbers);
// no_match = the backend found NO pricing rule (a 404) — an honest empty state,
// never a fabricated price; result = the backend's computed breakdown + trace,
// rendered verbatim. The FE never decides the price OR which rule won.
type QuoteState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "no_match" }
  | { status: "result"; quote: QuoteResponse };

// The IANA token regex a real zone must match ("UTC" or "Area/Location").
const IANA_ZONE = /^(UTC|[A-Za-z]+(?:\/[A-Za-z0-9_+-]+)+)$/;

// The zone we anchor the operator's wall-clock to. The backend's
// quote_reservation reprojects the absolute instant into the FACILITY's zone
// (and ignores the request `timezone` field), so an operator sitting in a
// DIFFERENT browser zone must NOT have their wall-clock interpreted in the
// browser zone — that misprices the window (#1361). We anchor on the facility's
// own timezone. If the facility has no timezone on record we fall back to the
// browser's resolved IANA zone (NOT a hardcoded "UTC"): that keeps the sent
// `(instant, timezone)` pair SELF-CONSISTENT — the instant read back in the
// declared zone reproduces the wall-clock — instead of the old mismatch where
// the instant came from the browser zone but the field claimed "UTC".
function resolveQuoteZone(facilityTimezone?: string | null): string {
  const tz = facilityTimezone?.trim();
  if (tz && IANA_ZONE.test(tz)) return tz;
  const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return browser && IANA_ZONE.test(browser) ? browser : "UTC";
}

// A datetime-local wall-clock string ("2026-07-06T18:00") interpreted IN A
// SPECIFIC IANA ZONE → the matching tz-aware UTC ISO instant. Unlike
// `new Date(value)` (which interprets the value in the BROWSER zone), this maps
// the wall-clock to the instant it denotes in `timeZone`, so the returned
// instant — when read back in `timeZone` — reproduces the same wall-clock.
//
// Method: take the wall-clock as a provisional UTC instant, format THAT instant
// in the target zone, measure the gap between what the zone shows and the
// wall-clock we wanted, and correct by that offset. Two passes make it exact
// across DST transitions (the offset can change mid-correction). Returns null
// for an empty / unparseable value so the caller can guard before sending.
function wallClockToInstantIso(value: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(
    value,
  );
  if (!m) return null;
  const [year, month, day, hour, minute, second] = [
    Number(m[1]),
    Number(m[2]),
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6] ?? 0),
  ];
  const targetUtcMs = Date.UTC(year, month - 1, day, hour, minute, second);
  if (Number.isNaN(targetUtcMs)) return null;

  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  // The UTC ms that the given instant *displays as* in `timeZone`.
  const shownAsUtcMs = (instantMs: number): number => {
    const parts = fmt.formatToParts(new Date(instantMs)).reduce(
      (acc, p) => {
        if (p.type !== "literal") acc[p.type] = p.value;
        return acc;
      },
      {} as Record<string, string>,
    );
    let h = Number(parts.hour);
    if (h === 24) h = 0; // some engines render midnight as "24"
    return Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      h,
      Number(parts.minute),
      Number(parts.second),
    );
  };

  let guess = targetUtcMs;
  for (let i = 0; i < 2; i++) {
    const offset = guess - shownAsUtcMs(guess);
    guess = targetUtcMs + offset;
  }
  const result = new Date(guess);
  return Number.isNaN(result.getTime()) ? null : result.toISOString();
}

function useScopedState<T>(scopeToken: string, initial: T) {
  const initialValue = useRef(initial);
  const activeScope = useRef(scopeToken);
  activeScope.current = scopeToken;
  const [record, setRecord] = useState({ scopeToken, value: initial });
  const setValue = useCallback(
    (value: SetStateAction<T>) => {
      if (activeScope.current !== scopeToken) return;
      setRecord((previous) =>
        activeScope.current !== scopeToken
          ? previous
          : {
              scopeToken,
              value:
                typeof value === "function"
                  ? (value as (current: T) => T)(
                      previous.scopeToken === scopeToken
                        ? previous.value
                        : initialValue.current,
                    )
                  : value,
            },
      );
    },
    [scopeToken],
  );
  return [
    record.scopeToken === scopeToken ? record.value : initialValue.current,
    setValue,
  ] as const;
}

export function RatesWorkspace() {
  const authorityGeneration = useProgramPreviewIdentity();
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const bindingToken = JSON.stringify([authorityGeneration, facilityId]);
  const activation = useRef({ bindingToken, epoch: 0 });
  if (activation.current.bindingToken !== bindingToken) {
    activation.current = { bindingToken, epoch: activation.current.epoch + 1 };
  }
  const scopeToken = JSON.stringify([bindingToken, activation.current.epoch]);
  const activeScopeRef = useRef(scopeToken);
  activeScopeRef.current = scopeToken;
  const [loadedScope, setLoadedScope] = useState(scopeToken);
  const [storedRows, setRows] = useState<PricingRule[]>([]);
  const [storedCourts, setCourts] = useState<CourtOption[]>([]);
  const [storedPlans, setPlans] = useState<PlanOption[]>([]);
  const [storedLoading, setLoading] = useState(true);
  const [storedError, setError] = useState<string | null>(null);
  const scopeMatches = loadedScope === scopeToken;
  const rows = scopeMatches ? storedRows : [];
  const courts = scopeMatches ? storedCourts : [];
  const plans = scopeMatches ? storedPlans : [];
  const loading = !scopeMatches || storedLoading;
  const error = scopeMatches ? storedError : null;

  // The Simple | Advanced toggle. BOTH modes read the SAME `rows` rules, so the
  // switch is lossless. Default = Advanced (the full P1 rule editor) so the
  // power user lands on the editor; the 90% operator clicks Simple for the
  // guided absolute-price band form. Simple GENERATES source:"simple" rules and
  // never touches a source:"advanced" rule.
  const [mode, setMode] = useState<"simple" | "advanced">("advanced");

  // Editor (create / edit). undefined = closed.
  const [editorRecord, setEditorRecord] = useState<{
    scopeToken: string;
    value: EditorState;
  }>({ scopeToken, value: undefined });
  const editor =
    editorRecord.scopeToken === scopeToken ? editorRecord.value : undefined;
  const setEditor = useCallback(
    (value: EditorState) => {
      if (activeScopeRef.current === scopeToken)
        setEditorRecord({ scopeToken, value });
    },
    [scopeToken],
  );

  const [pendingDelete, setPendingDelete] = useScopedState<PricingRule | null>(
    scopeToken,
    null,
  );
  const [deleting, setDeleting] = useScopedState(scopeToken, false);
  const ruleDeleteInFlight = useRef<string | null>(null);

  // Quote-preview panel. The operator picks a court + window + party makeup and
  // the backend returns a computed breakdown — the FE renders ONLY those
  // numbers, it never computes a price or sums line items.
  const [quoteOpen, setQuoteOpen] = useScopedState(scopeToken, false);
  const [quoteCourtId, setQuoteCourtId] = useScopedState(scopeToken, "");
  const [quoteStart, setQuoteStart] = useScopedState(scopeToken, "");
  const [quoteEnd, setQuoteEnd] = useScopedState(scopeToken, "");
  const [quotePlayerCount, setQuotePlayerCount] = useScopedState(
    scopeToken,
    "2",
  );
  const [quoteNonMemberCount, setQuoteNonMemberCount] = useScopedState(
    scopeToken,
    "0",
  );
  const [quotePlanId, setQuotePlanId] = useScopedState(scopeToken, NO_PLAN);
  const [quoteState, setQuoteState] = useScopedState<QuoteState>(scopeToken, {
    status: "idle",
  });
  // Re-entrancy guard — a rapid double-submit must fire the quote ONCE. Mirrors
  // overrideInFlight: a synchronous ref so the second click sees the in-flight
  // flag set by the first (a state flag would lag a render behind).
  const quoteInFlight = useRef<string | null>(null);

  // Per-rule "Member pricing" expand + lazy override state, keyed by rule_id. A
  // sibling toggle to the rule row — the overrides load LAZILY on first expand
  // only (never on page load), mirroring the leagues registrations/standings
  // expands exactly.
  const [overridesOpen, setOverridesOpen] = useScopedState<
    Record<string, boolean>
  >(scopeToken, {});
  const [overrides, setOverrides] = useScopedState<
    Record<string, OverrideState>
  >(scopeToken, {});

  // The per-rule add-override form: which rule's form is open, the chosen plan,
  // the typed member price, and a re-entrancy guard so a double-submit can't fire
  // two POSTs. Scoped to one rule at a time (a single inline form).
  const [addRuleId, setAddRuleId] = useScopedState<string | null>(
    scopeToken,
    null,
  );
  const [addPlanId, setAddPlanId] = useScopedState(scopeToken, "");
  const [addPrice, setAddPrice] = useScopedState(scopeToken, "");
  const [savingOverride, setSavingOverride] = useScopedState(scopeToken, false);
  const overrideInFlight = useRef<string | null>(null);

  // Pending per-row override delete (confirm dialog, mirroring the rule delete).
  const [pendingOverrideDelete, setPendingOverrideDelete] =
    useScopedState<MembershipPriceOverride | null>(scopeToken, null);
  const [deletingOverride, setDeletingOverride] = useScopedState(
    scopeToken,
    false,
  );
  const overrideDeleteInFlight = useRef<string | null>(null);

  // Name lookup so the list summary renders plan NAMES, never raw UUIDs.
  const planNameById = useMemo(() => {
    const m = new Map<string, string>();
    plans.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [plans]);

  const refresh = useCallback(
    async (id: string) => {
      const capturedScope = scopeToken;
      if (activeScopeRef.current !== capturedScope || id !== facilityId) return;
      // Server order — pricing precedence is the backend's concern. No client sort.
      const refreshed = await listPricingRules(id);
      if (activeScopeRef.current !== capturedScope) return;
      setRows(refreshed);
      setLoadedScope(capturedScope);
    },
    [scopeToken, facilityId],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadedScope(scopeToken);
    setError(null);
    setRows([]);
    setCourts([]);
    setPlans([]);
    setEditor(undefined);
    setPendingDelete(null);
    setOverridesOpen({});
    setOverrides({});
    setAddRuleId(null);
    if (!facilityId) return;
    setLoading(true);
    (async () => {
      // Rules drive the list; courts and plans drive the editor's fields.
      // Class labels resolve only for the current visible targets. An OPTION
      // source must not blank the whole page, so they settle independently.
      // Only a rules-load failure surfaces the page error.
      const [rulesRes, courtsRes, plansRes] = await Promise.allSettled([
        listPricingRules(facilityId),
        listCourts(facilityId),
        listMembershipPlans(facilityId),
      ]);
      if (cancelled || activeScopeRef.current !== scopeToken) return;
      if (rulesRes.status === "fulfilled") {
        setRows(rulesRes.value);
        setError(null);
      } else {
        const err = rulesRes.reason;
        setError(err instanceof Error ? err.message : "Failed to load rates");
      }
      if (courtsRes.status === "fulfilled") {
        setCourts(
          courtsRes.value.map((c) => ({ id: c.court_id, name: c.name })),
        );
      }
      if (plansRes.status === "fulfilled") {
        setPlans(plansRes.value.map((p) => ({ id: p.plan_id, name: p.name })));
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, authorityGeneration, scopeToken, setEditor]);

  const confirmDelete = async () => {
    if (!pendingDelete || !facilityId || deleting || !scopeMatches) return;
    const capturedScope = scopeToken;
    if (ruleDeleteInFlight.current === capturedScope) return;
    ruleDeleteInFlight.current = capturedScope;
    setDeleting(true);
    try {
      await deletePricingRule(pendingDelete.rule_id);
      if (activeScopeRef.current !== capturedScope) return;
      await refresh(facilityId);
      setPendingDelete(null);
    } catch (err) {
      if (activeScopeRef.current !== capturedScope) return;
      // A failed delete must NOT remove the row — notifications and leave it in place.
      notifications.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      if (ruleDeleteInFlight.current === capturedScope)
        ruleDeleteInFlight.current = null;
      setDeleting(false);
    }
  };

  // Lazy-load a rule's member-price overrides on FIRST expand only (avoids an
  // N+1 eager fetch across every rule on page mount). Mirrors the leagues
  // registrations/standings expands EXACTLY: a collapse keeps successfully-
  // loaded data so a re-expand is instant and does not re-fetch; a never-loaded
  // rule — or one whose previous fetch ERRORED — fetches on (re-)expand, so a
  // transient failure is recoverable without a page reload. The cache guard
  // short-circuits on {loading || entries} but deliberately NOT on {error}.
  const toggleOverrides = (rule: PricingRule) => {
    if (!scopeMatches || !facilityId || !rows.includes(rule)) return;
    const capturedScope = scopeToken;
    const id = rule.rule_id;
    const isOpen = !!overridesOpen[id];
    setOverridesOpen((prev) => ({ ...prev, [id]: !isOpen }));
    if (isOpen) {
      // Collapsing — close any open add form for this rule.
      if (addRuleId === id) closeAddOverride();
      return;
    }
    const existing = overrides[id];
    if (existing && (existing.loading || existing.entries)) return;
    setOverrides((prev) => ({ ...prev, [id]: { loading: true } }));
    if (!facilityId) return;
    (async () => {
      const [overridesRes, plansRes] = await Promise.allSettled([
        listPriceOverrides(facilityId, id),
        listMembershipPlans(facilityId),
      ]);
      if (activeScopeRef.current !== capturedScope) return;
      // Refresh the plan name lookup if the plans fetch succeeded — the override
      // rows resolve plan_id -> NAME through it. A plans failure leaves the prior
      // plans in place (never blanks the lookup).
      if (plansRes.status === "fulfilled") {
        setPlans(plansRes.value.map((p) => ({ id: p.plan_id, name: p.name })));
      }
      if (overridesRes.status === "rejected") {
        setOverrides((prev) => ({
          ...prev,
          [id]: {
            loading: false,
            error:
              "We couldn't load the member overrides for this rule. Please try again.",
          },
        }));
        return;
      }
      setOverrides((prev) => ({
        ...prev,
        [id]: { loading: false, entries: overridesRes.value },
      }));
    })();
  };

  const openAddOverride = (ruleId: string) => {
    if (!scopeMatches || !rows.some((rule) => rule.rule_id === ruleId)) return;
    setAddRuleId(ruleId);
    setAddPlanId("");
    setAddPrice("");
  };

  const closeAddOverride = () => {
    setAddRuleId(null);
    setAddPlanId("");
    setAddPrice("");
  };

  // The add button is gated on the same rules the handler enforces (defence in
  // depth): a chosen plan and a member price matching the 2-decimal MONEY shape.
  const addOverrideValid = addPlanId !== "" && MONEY.test(addPrice.trim());

  const handleAddOverride = async () => {
    if (!facilityId || !addRuleId || !scopeMatches) return;
    const capturedScope = scopeToken;
    const price = addPrice.trim();
    // Client-side gates — never round-trip an invalid POST. The backend remains
    // the authority on pricing; this only blocks the obvious bad-shape input.
    if (!addPlanId) return;
    if (!MONEY.test(price)) return;
    if (overrideInFlight.current === capturedScope) return;
    overrideInFlight.current = capturedScope;
    setSavingOverride(true);
    const ruleId = addRuleId;
    try {
      const created = await createPriceOverride(facilityId, {
        rule_id: ruleId,
        plan_id: addPlanId,
        member_price: price,
      });
      if (activeScopeRef.current !== capturedScope) return;
      // Append the server-returned row (never optimistically fabricate one).
      setOverrides((prev) => {
        const cur = prev[ruleId];
        return {
          ...prev,
          [ruleId]: {
            loading: false,
            entries: [...(cur?.entries ?? []), created],
          },
        };
      });
      notifications.success("Member price added.");
      closeAddOverride();
    } catch (err) {
      if (activeScopeRef.current !== capturedScope) return;
      // Failure (incl. 403 RBAC + the 409 UNIQUE(rule, plan) duplicate): honest
      // notifications, form stays open, NO phantom row injected.
      notifications.error(
        err instanceof Error ? err.message : "Could not add the member price.",
      );
    } finally {
      if (overrideInFlight.current === capturedScope)
        overrideInFlight.current = null;
      setSavingOverride(false);
    }
  };

  const confirmOverrideDelete = async () => {
    if (!pendingOverrideDelete || deletingOverride || !scopeMatches) return;
    const capturedScope = scopeToken;
    const target = pendingOverrideDelete;
    if (overrideDeleteInFlight.current === capturedScope) return;
    overrideDeleteInFlight.current = capturedScope;
    setDeletingOverride(true);
    try {
      await deletePriceOverride(target.override_id);
      if (activeScopeRef.current !== capturedScope) return;
      // Drop the removed row from its rule's cached entries.
      setOverrides((prev) => {
        const cur = prev[target.rule_id];
        if (!cur?.entries) return prev;
        return {
          ...prev,
          [target.rule_id]: {
            ...cur,
            entries: cur.entries.filter(
              (o) => o.override_id !== target.override_id,
            ),
          },
        };
      });
      notifications.success("Member price removed.");
      setPendingOverrideDelete(null);
    } catch (err) {
      if (activeScopeRef.current !== capturedScope) return;
      // A failed delete must NOT remove the row — notifications and leave it in place.
      notifications.error(
        err instanceof Error
          ? err.message
          : "Could not remove the member price.",
      );
    } finally {
      if (overrideDeleteInFlight.current === capturedScope)
        overrideDeleteInFlight.current = null;
      setDeletingOverride(false);
    }
  };

  const openQuote = () => {
    if (!scopeMatches) return;
    setQuoteState({ status: "idle" });
    setQuoteOpen(true);
  };

  // Run the simulation. ALL the validation here is presence/shape only so the
  // operator isn't bounced by an uninterpretable backend error — the backend
  // remains the authority on pricing, the rule ranking, and the cross-field
  // rules. The request asks the backend to EXPLAIN (`explain: true`), so the
  // response carries the rule-by-rule trace ("why this price"). On a 404 (no
  // matching rule) we show an honest empty state; on a 422 the backend message
  // verbatim; on any other failure (403, network) an honest message — never a
  // fabricated price, breakdown, trace, or Winner.
  const submitQuote = async () => {
    if (!facilityId || !scopeMatches) return;
    const capturedScope = scopeToken;
    // Re-entrancy: a double-submit while a request is in flight is a no-op.
    if (quoteInFlight.current === capturedScope) return;

    if (!quoteCourtId) {
      setQuoteState({
        status: "error",
        message: "A court is required to preview a price.",
      });
      return;
    }
    const players = Number(quotePlayerCount);
    if (!Number.isFinite(players) || players < 1) {
      setQuoteState({
        status: "error",
        message: "Player count must be at least 1.",
      });
      return;
    }
    const nonMembers = Number(quoteNonMemberCount || "0");
    if (Number.isFinite(nonMembers) && nonMembers > players) {
      setQuoteState({
        status: "error",
        message: "Non-members cannot exceed the player count.",
      });
      return;
    }
    // Anchor the operator's wall-clock to the FACILITY's zone (not the browser's)
    // so the sent (start_time, timezone) pair is self-consistent and the backend
    // — which prices in the facility zone — reads the hour the operator intended.
    const quoteZone = resolveQuoteZone(facility?.timezone);
    const startIso = wallClockToInstantIso(quoteStart, quoteZone);
    const endIso = wallClockToInstantIso(quoteEnd, quoteZone);
    if (!startIso || !endIso) {
      setQuoteState({
        status: "error",
        message: "Enter a valid start and end time.",
      });
      return;
    }
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      setQuoteState({
        status: "error",
        message: "The end time must be after the start time.",
      });
      return;
    }

    const body: ReservationQuoteRequest = {
      court_id: quoteCourtId,
      start_time: startIso,
      end_time: endIso,
      // The instant was derived by interpreting the wall-clock in this exact
      // zone, so the field truthfully names the zone the instant denotes —
      // reading start_time back in `timezone` reproduces the operator's
      // wall-clock (the self-consistency the backend's facility-zone repricing
      // relies on; #1361).
      timezone: quoteZone,
      player_count: players,
      non_member_player_count: Number.isFinite(nonMembers) ? nonMembers : 0,
      court_type: "any",
      player_category: "any",
      // explain:true is the heart of the simulator — it asks the backend to
      // return the rule-by-rule trace. Without it there is no "why this price".
      explain: true,
      // "None" → omit plan_id entirely (never a "none" sentinel string).
      ...(quotePlanId !== NO_PLAN ? { plan_id: quotePlanId } : {}),
    };

    // Set the in-flight guard only AFTER validation passes — a validation
    // early-return above must never leave the guard stuck.
    quoteInFlight.current = capturedScope;
    setQuoteState({ status: "loading" });
    try {
      const quote = await getReservationQuote(facilityId, body);
      if (activeScopeRef.current !== capturedScope) return;
      setQuoteState({ status: "result", quote });
    } catch (err) {
      if (activeScopeRef.current !== capturedScope) return;
      // 404 (PricingRuleNotFoundError) → an honest "no rule matches" empty
      // state, NOT a money figure and NOT a phantom Winner. 422 → the backend's
      // validation message verbatim. Any other failure (403 RBAC, network, 500)
      // → the error's own message. Either way: no fabricated breakdown — the
      // prior result is cleared by the new state.
      if (isApiError(err) && err.status === 404) {
        setQuoteState({ status: "no_match" });
      } else {
        const message =
          err instanceof Error ? err.message : "Could not get a price preview.";
        setQuoteState({ status: "error", message });
      }
    } finally {
      if (quoteInFlight.current === capturedScope) quoteInFlight.current = null;
    }
  };

  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading rates workspace"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
          px: { xs: 2, lg: 3 },
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ px: { xs: 2, lg: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" variant="h5">
            Rates
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {facility?.name ?? "Booking rates and pricing rules."}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", minWidth: 0, rowGap: 1 }}
        >
          {/* Both modes read the same authoritative rules, so switching is
              lossless. */}
          <ToggleButtonGroup
            exclusive
            aria-label="Rates mode"
            onChange={(_event, nextMode: "simple" | "advanced" | null) => {
              if (nextMode) setMode(nextMode);
            }}
            size="small"
            value={mode}
          >
            <ToggleButton value="simple" aria-label="Simple">
              Simple
            </ToggleButton>
            <ToggleButton value="advanced" aria-label="Advanced">
              Advanced
            </ToggleButton>
          </ToggleButtonGroup>
          {mode === "advanced" ? (
            <>
              <Button
                disabled={loading}
                onClick={openQuote}
                size="small"
                startIcon={<CalculateOutlinedIcon />}
                variant="outlined"
              >
                Preview price
              </Button>
              <Button
                disabled={loading}
                onClick={() => setEditor(null)}
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
              >
                New rate
              </Button>
            </>
          ) : null}
        </Stack>
      </Stack>

      <Paper variant="outlined">
        <Stack spacing={2} sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Typography component="h2" variant="h6">
            {mode === "simple" ? "Simple rates" : "Configured rates"}
          </Typography>
          {mode === "simple" ? (
            <SimpleRatesMode
              currency="USD"
              facilityId={facilityId}
              onSaved={() => refresh(facilityId)}
              rules={rows}
            />
          ) : (
            <RatesList
              authorityGeneration={authorityGeneration}
              addRuleId={addRuleId}
              addOverrideValid={addOverrideValid}
              addPlanId={addPlanId}
              addPrice={addPrice}
              courts={courts}
              error={error}
              facilityId={facilityId}
              loading={loading}
              onCloseAddOverride={closeAddOverride}
              onDelete={(rule) => setPendingDelete(rule)}
              onDeleteOverride={(override) =>
                setPendingOverrideDelete(override)
              }
              onEdit={(rule) => setEditor(rule)}
              onOpenAddOverride={openAddOverride}
              onPlanChange={setAddPlanId}
              onPriceChange={setAddPrice}
              onReordered={() => refresh(facilityId)}
              onSubmitAddOverride={handleAddOverride}
              onToggleOverrides={toggleOverrides}
              overrides={overrides}
              overridesOpen={overridesOpen}
              planNameById={planNameById}
              plans={plans}
              rules={rows}
              savingOverride={savingOverride}
            />
          )}
        </Stack>
      </Paper>

      {/* Focused grouped rule editor (create OR edit) */}
      <RuleEditor
        key={`${authorityGeneration}:${facilityId}`}
        authorityGeneration={authorityGeneration}
        courts={courts}
        facilityId={facilityId}
        onOpenChange={(open) => {
          if (!open) setEditor(undefined);
        }}
        onSaved={() => refresh(facilityId)}
        open={editor !== undefined}
        plans={plans}
        rule={editor ?? null}
      />

      {/* Price simulator — final price + the "why this price" rule trace */}
      <Dialog
        aria-describedby="quote-dialog-description"
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setQuoteOpen(false);
          setQuoteState({ status: "idle" });
        }}
        open={quoteOpen}
        slotProps={{ paper: { sx: { maxHeight: "85vh" } } }}
      >
        <DialogTitle>Court reservation price simulator</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText id="quote-dialog-description">
              Pick a scenario — the price and the rule it matched are computed
              by the backend.
            </DialogContentText>

            <FormControl fullWidth size="small">
              <InputLabel id="quote-court-label">Court</InputLabel>
              <Select
                id="quote-court"
                label="Court"
                labelId="quote-court-label"
                onChange={(event) => setQuoteCourtId(event.target.value)}
                value={quoteCourtId}
              >
                {courts.map((court) => (
                  <MenuItem key={court.id} value={court.id}>
                    {court.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <TextField
                id="quote-start"
                label="Start"
                onChange={(event) => setQuoteStart(event.target.value)}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type="datetime-local"
                value={quoteStart}
              />
              <TextField
                id="quote-end"
                label="End"
                onChange={(event) => setQuoteEnd(event.target.value)}
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                type="datetime-local"
                value={quoteEnd}
              />
              <TextField
                id="quote-players"
                label="Player count"
                onChange={(event) => setQuotePlayerCount(event.target.value)}
                size="small"
                slotProps={{ htmlInput: { min: 1 } }}
                type="number"
                value={quotePlayerCount}
              />
              <TextField
                id="quote-non-members"
                label="Non-members"
                onChange={(event) => setQuoteNonMemberCount(event.target.value)}
                size="small"
                slotProps={{ htmlInput: { min: 0 } }}
                type="number"
                value={quoteNonMemberCount}
              />
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel id="quote-plan-label">Plan</InputLabel>
              <Select
                id="quote-plan"
                label="Plan"
                labelId="quote-plan-label"
                onChange={(event) => setQuotePlanId(event.target.value)}
                value={quotePlanId}
              >
                <MenuItem value={NO_PLAN}>None</MenuItem>
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {quoteState.status === "error" ? (
              <Alert severity="error">{quoteState.message}</Alert>
            ) : null}

            {quoteState.status === "no_match" ? (
              <Paper
                sx={{ borderStyle: "dashed", p: 2, textAlign: "center" }}
                variant="outlined"
              >
                <Typography color="text.secondary" variant="body2">
                  No pricing rule matches this scenario. Add or adjust a rate so
                  this court and window are covered.
                </Typography>
              </Paper>
            ) : null}

            {quoteState.status === "loading" ? (
              <Paper role="status" sx={{ p: 1.5 }} variant="outlined">
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: "center" }}
                >
                  <CircularProgress aria-hidden size={18} />
                  <Typography color="text.secondary" variant="body2">
                    Calculating price…
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            {quoteState.status === "result" ? (
              <Stack spacing={2}>
                {/* Final price + the charge build-up, rendered from the response
                    VERBATIM (formatMoney; the FE never sums line items). */}
                <Paper sx={{ p: 2 }} variant="outlined">
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Line total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(quoteState.quote.line_items ?? []).map((line, i) => (
                          <TableRow key={`${line.code}-${i}`}>
                            <TableCell>{line.label}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "text.secondary" }}
                            >
                              {parseDecimal(line.amount) === 0
                                ? "Free"
                                : formatMoney(
                                    line.amount,
                                    quoteState.quote.currency,
                                  )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: "text.secondary" }}
                            >
                              {line.quantity}
                            </TableCell>
                            <TableCell align="right">
                              {formatMoney(
                                line.total,
                                quoteState.quote.currency,
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Stack
                    spacing={0.5}
                    sx={{
                      borderTop: 1,
                      borderColor: "divider",
                      mt: 1.5,
                      pt: 1.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography color="text.secondary" variant="body2">
                        Subtotal
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {formatMoney(
                          quoteState.quote.subtotal,
                          quoteState.quote.currency,
                        )}
                      </Typography>
                    </Stack>
                    {/* The TOTAL is the response's total VERBATIM — it can differ
                        from the line-item sum (e.g. a member discount). A client
                        summing lines would lie about the price. */}
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        Total
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {formatMoney(
                          quoteState.quote.total,
                          quoteState.quote.currency,
                        )}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>

                {/* "Why this price" — the rule-by-rule trace. The SERVER decided
                    which rule won, its priority order, and the tiebreak reason;
                    the FE only renders it. Each candidate shows by NAME (or the
                    server-built summary when unnamed) — never a raw rule_id. */}
                {quoteState.quote.trace &&
                (quoteState.quote.trace.candidates?.length ?? 0) > 0 ? (
                  <Paper sx={{ p: 2 }} variant="outlined">
                    <Typography sx={{ fontWeight: 700, mb: 1 }} variant="body2">
                      Why this price
                    </Typography>
                    <Stack
                      component="ul"
                      spacing={1}
                      sx={{ listStyle: "none", m: 0, p: 0 }}
                    >
                      {(quoteState.quote.trace.candidates ?? []).map((c) => {
                        // Named rules render by NAME; an unnamed rule falls back
                        // to the SERVER-built summary. A raw rule_id is NEVER a
                        // label.
                        const label = c.rule_name?.trim() || c.summary;
                        return (
                          <Box
                            component="li"
                            key={c.rule_id}
                            data-rule-trace-entry
                            sx={{
                              bgcolor: c.won ? "primary.main" : "transparent",
                              border: 1,
                              borderColor: c.won ? "primary.main" : "divider",
                              borderRadius: 1,
                              color: c.won
                                ? "primary.contrastText"
                                : "text.primary",
                              p: 1.25,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{ fontWeight: 700 }}
                                variant="body2"
                              >
                                {label}
                              </Typography>
                              {c.won ? (
                                <Chip
                                  color="primary"
                                  label="Winner"
                                  size="small"
                                />
                              ) : null}
                            </Stack>
                            {/* The server-built summary as supporting context
                                (only when a name was shown above — otherwise the
                                summary already IS the label). */}
                            {c.rule_name?.trim() ? (
                              <Typography
                                color={c.won ? "inherit" : "text.secondary"}
                                sx={{ mt: 0.5, opacity: c.won ? 0.85 : 1 }}
                                variant="caption"
                              >
                                {c.summary}
                              </Typography>
                            ) : null}
                            {/* The tiebreak reason + priority in one line. The
                                reason renders first and the priority LAST so the
                                element's text never ends with another rule's
                                name — a loser's "…the winning rule" reason must
                                not be mistaken for a "Winning rule" badge. */}
                            <Typography
                              color={c.won ? "inherit" : "text.secondary"}
                              sx={{
                                display: "block",
                                mt: 0.5,
                                opacity: c.won ? 0.85 : 1,
                              }}
                              variant="caption"
                            >
                              {c.tiebreak_reason}
                              {" · Priority "}
                              {c.priority}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>
                ) : null}
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setQuoteOpen(false);
              setQuoteState({ status: "idle" });
            }}
            size="small"
            type="button"
          >
            Close
          </Button>
          <Button
            disabled={quoteState.status === "loading"}
            onClick={submitQuote}
            size="small"
            type="button"
            variant="contained"
          >
            Get quote
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete rule confirm */}
      <Dialog
        aria-describedby="delete-rate-description"
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
        open={Boolean(pendingDelete)}
      >
        <DialogTitle>Delete rate?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-rate-description">
            This permanently removes this pricing rule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deleting}
            onClick={() => setPendingDelete(null)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deleting}
            onClick={confirmDelete}
            size="small"
            variant="contained"
          >
            {deleting ? "Deleting…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove member-price override confirm */}
      <Dialog
        aria-describedby="remove-member-price-description"
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (!deletingOverride) setPendingOverrideDelete(null);
        }}
        open={Boolean(pendingOverrideDelete)}
      >
        <DialogTitle>Remove member price?</DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-member-price-description">
            This removes the member price override for this plan on this rule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={deletingOverride}
            onClick={() => setPendingOverrideDelete(null)}
            size="small"
          >
            Cancel
          </Button>
          <Button
            color="error"
            disabled={deletingOverride}
            onClick={confirmOverrideDelete}
            size="small"
            variant="contained"
          >
            {deletingOverride ? "Removing…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
